import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { tveService } from '#/client/api';
import { Mvpd, MvpdAuthRequest, TokenResponse } from '#/client/models';
import { ApiError } from '#/errors/serviceErrors';
import { useNavigation } from '#/hooks/useNavigation';
import { useRemoveQueryStringParams } from '#/hooks/useRemoveQueryStringParams';
import { StorageKeys } from '#/service/constants';
import { Path } from '#/service/routes';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { ignoreError, noop, uniqBy } from '#/service/utils';
import { authActions, useAuthStore } from '#/store/auth/auth.store';

// 10 seconds polling interval to be used
const POLLING_MS = 10 * 1000;

const getBaseAuthRequest = (
  secondScreenCode: string | null
): MvpdAuthRequest => {
  // for second screen, we're overwriting the device ID in deviceInfo, but that doesn't get stored
  // so getDeviceInfo will still return the normal one next time
  const deviceInfo =
    (secondScreenCode && getDeviceInfo(crypto.randomUUID())) || getDeviceInfo();

  return {
    networkInfo: {
      type: 'WIFI', // We can't reliably get this from the browser. So we'll just assume WIFI. It feels it does not affect functionality
    },
    deviceInfo,
    ...(secondScreenCode && { secondScreen: { code: secondScreenCode } }),
  };
};

/**
 * Handles TVE authentication process.
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#flow
 * @param [closeModal] {Function} Callback to close the modal. Default noop.
 * @param [isSecondScreenLogin] {boolean} Boolean for second screen login. Default false.
 * @returns API to work with the authentication process on the Login page
 */
export function useLoginTve(
  closeModal: () => void = noop,
  isSecondScreenLogin = false
) {
  const [mvpds, setMvpds] = useState<GroupedMvpds>();
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState<boolean>(true);
  const [awaiting, setAwaiting] = useState<boolean>(false);
  const secondScreenCode =
    sessionStorage.getItem(StorageKeys.SECOND_SCREEN_CODE) ?? '';
  const { go } = useNavigation('useLoginTve');

  const logUserIn = useCallback((authResponse: TokenResponse) => {
    authActions.login(authResponse).finally(() => {
      setAwaiting(false);
      closeModal();
    });
    // `closeModal` is not here on purpose. closeModal has a new reference every time,
    // so it would generate an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentBaseAuthRequest = useMemo(
    () => getBaseAuthRequest(secondScreenCode),
    [secondScreenCode]
  );

  useEffect(() => {
    setLoading(true);

    tveService
      .authenticate(currentBaseAuthRequest)
      .then((res) => {
        const { type, auth, secondScreen } = res.data._meta;

        if (type === 'success') {
          // in case a user tries to log into a primary device which is already logged in
          // direct them to the success page
          if (isSecondScreenLogin) {
            return go(Path.TveSecondScreenLogin, {
              complete: true,
              error: false,
            });
          }

          return logUserIn(auth!);
        }

        // we'll need this to complete the flow after the redirect
        if (type === 'second-screen' && secondScreen?.authorizationCode) {
          sessionStorage.setItem(
            StorageKeys.TVE_CODE,
            secondScreen.authorizationCode
          );
        }

        return tveService
          .getMvpds()
          .then((res) => setMvpds(groupMvpds(res.data.mvpds)));
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [
    currentBaseAuthRequest,
    go,
    isSecondScreenLogin,
    logUserIn,
    secondScreenCode,
  ]);

  useEffect(() => {
    // Do a polling loop to check if the user is authenticated
    let interval: ReturnType<typeof setInterval>;

    if (awaiting && !secondScreenCode) {
      interval = setInterval(() => {
        tveService
          .authenticate(currentBaseAuthRequest)
          .then((res) => {
            const meta = res.data._meta;

            if (meta.type === 'success') {
              logUserIn(meta.auth!);
            } else if (meta.type === 'redirect') {
              restartProcess();
            }
          })
          .catch((err) => {
            setError(err);
            setAwaiting(false);
          });
      }, POLLING_MS);
    }

    return () => clearInterval(interval);
  }, [
    awaiting,
    closeModal,
    currentBaseAuthRequest,
    logUserIn,
    secondScreenCode,
  ]);

  /**
   * Starts authentication process. It opens a new tab and starts polling to get the authentication back
   * @param mvpdId Selected MVPD
   */
  function selectProvider(mvpdId: string) {
    if (!mvpdId) {
      return;
    }

    setAwaiting(true);
    tveService
      .authenticate({ ...currentBaseAuthRequest, mvpdId })
      .then((res) => {
        const meta = res.data._meta;
        const targetUrl = meta.redirect?.targetUrl;

        // we'll need this to complete the flow after the redirect
        sessionStorage.setItem(StorageKeys.TVE_MVPD, mvpdId);

        if (meta.type === 'redirect') {
          if (targetUrl) {
            window.location.href = targetUrl;
          } else {
            throw new Error(`URL for a redirect wasn't provided from the Core`);
          }
        } else {
          throw new Error(
            `We don't support authentication method ${meta.type}`
          );
        }
      })
      .catch((err) => setError(err));
  }

  function restartProcess() {
    setAwaiting(false);
  }

  return {
    mvpds,
    error,
    loading,
    selectProvider,
    awaiting,
    restartProcess,
  };
}

function groupMvpds(allMvpds: Mvpd[]): GroupedMvpds {
  return {
    promoted: uniqBy(
      allMvpds.filter((mvpd) => mvpd.promoted),
      (el) => el.id
    ),
    others: uniqBy(allMvpds, (el) => el.id),
  };
}

interface GroupedMvpds {
  promoted: Mvpd[];
  others: Mvpd[];
}

/**
 * The TVE login flow includes a redirect to an external site. After the external login, the user is redirected back
 * to us, with `one_status=success`. That will be a new page load, so all our state will be lost. This hook listens for
 * this case, gets the required data back from session storage, and uses it to complete the login flow. This is what
 * gets the ONE tokens saved, and the user fully logged in.
 * In case of second screen login, it should only notify the user about logging into the primary device was successful,
 * user won't be logged in to the web app (secondary device).
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#flow
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/second-screen/#tve-authentication
 * @returns void
 */
export const useLoginTveComplete = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { go } = useNavigation('useLoginTveComplete');
  const { isSecondScreen: isSecondScreenConfigured } = useAuthStore();
  const { removeQueryParams } = useRemoveQueryStringParams();

  useEffect(() => {
    // check the one_status query param
    const params = new URLSearchParams(search);
    const oneStatus = params.get('one_status');

    if (oneStatus !== 'success') {
      return;
    }

    // load & remove the values stored before the redirect
    const authorizationCode = sessionStorage.getItem(StorageKeys.TVE_CODE);
    const mvpdId = sessionStorage.getItem(StorageKeys.TVE_MVPD);
    const secondScreenCode = sessionStorage.getItem(
      StorageKeys.SECOND_SCREEN_CODE
    );
    sessionStorage.removeItem(StorageKeys.TVE_CODE);
    sessionStorage.removeItem(StorageKeys.TVE_MVPD);
    sessionStorage.removeItem(StorageKeys.SECOND_SCREEN_CODE);

    if (!(authorizationCode && mvpdId)) {
      return;
    }

    // check if second screen flow configured and actually used as well
    const isSecondScreenLogin = !!(
      isSecondScreenConfigured && secondScreenCode
    );
    const currentBaseAuthRequest = getBaseAuthRequest(secondScreenCode);

    // complete the TVE flow in case of second screen authentication
    if (isSecondScreenLogin) {
      console.debug('useLoginTveComplete - complete with second screen flow', {
        mvpdId,
        authorizationCode,
        secondScreenCode,
      });

      // check if the login to the primary device was successful
      tveService
        .authenticate({
          ...currentBaseAuthRequest,
          mvpdId,
          authorizationCode,
        })
        .then((res) => {
          const { type } = res.data._meta;

          if (type !== 'success') {
            console.debug(`Unexpected response type: ${type}`);
          }

          // show success message about login to the primary device
          // user should not be logged in on the secondary device (web app)
          // in case of error, and error message will be shown
          return go(Path.TveSecondScreenLogin, {
            complete: true,
            error: type !== 'success',
          });
        })
        .catch(ignoreError);
    } else {
      console.debug('useLoginTveComplete - complete with standard login flow', {
        mvpdId,
        authorizationCode,
      });

      // complete the standard TVE login flow
      tveService
        .authenticate({ ...currentBaseAuthRequest, mvpdId, authorizationCode })
        .then((res) => {
          const { type, auth } = res.data._meta;

          if (type !== 'success') {
            throw new Error(`Unexpected response type: ${type}`);
          }

          if (!auth) {
            throw new Error('No auth object received');
          }

          // log in the user to the web app
          return authActions.login(auth);
        })
        .catch(ignoreError)
        .then(() => {
          // successful or not, remove the one_status query param
          removeQueryParams(['one_status']);
        });
    }
  }, [
    go,
    isSecondScreenConfigured,
    navigate,
    pathname,
    removeQueryParams,
    search,
  ]);
};
